import { DictType } from "@pma/types"
import { useContext, useState } from "react"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { cn } from "@pma/utils/helpers"
import { Button } from "@components/common/button"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { useAuth } from "@stores/auth-store"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PRIORITY_DICTIONARY_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import useTranslator from "@hooks/use-translator"
import { editTaskByCurrentUser } from "@pma/utils/utils"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

interface Props {
  priority?: DictType
  taskId?: number
  setPriority?: any
  parentTaskId?: number
}
const TaskPriorityEditor = ({ priority, taskId, setPriority, parentTaskId }: Props) => {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()
  const [searchParams] = useSearchParams()

  const { setTaskExpandForLoading } = useContext(GroupedTasksContext)

  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParams)

  const [open, setOpen] = useState(false)
  const [selectedPriority, setSelectedPriority] = useState(priority)
  
  const getPriorities = async () => {
    const response = await axios.get(
      SERVER_URL + `${PRIORITY_DICTIONARY_ENDPOINT}?organization_id=${orgId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    )

    return response.data
  }

  const { data } = useQuery({
    queryKey: ["task-priorities", orgId],
    queryFn: getPriorities,
  })

  const updateTaskPriorityMutation = useMutation({
    mutationKey: ["update-task-priority"],
    mutationFn: (statusId: number) =>
      editTaskByCurrentUser(taskId || 0, {
        priority_id: statusId,
        inform_users: true,
      }),
    onSuccess: () => {
      // Trigger Revalidate tasks
      // queryClient.invalidateQueries({ queryKey: ['clear-selected-tasks'] })
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      if (parentTaskId) {
        setTaskExpandForLoading(parentTaskId, { toggleExpanded: () => {} } as any)
      }
    },
  })

  if (taskId) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='default'
            role='combobox'
            aria-expanded={open}
            className='h-6 w-fit flex-col items-center rounded-full text-[11px] text-black md:flex md:min-w-[80px]'
            style={
              selectedPriority
                ? {
                    background:
                      selectedPriority?.color === "#transparent"
                        ? undefined
                        : selectedPriority?.color,
                  }
                : { background: "transparent", border: "1px solid black" }
            }
          >
            {selectedPriority
              ? `${selectedPriority?.name} (${selectedPriority?.level || 0})`
              : "--"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[250px] p-0'>
          <Command>
            <CommandInput placeholder={translate("Search priority")} />
            <CommandList>
              <CommandEmpty>{translate("No priority found")}.</CommandEmpty>
              <CommandGroup>
                {data?.length ? (
                  [
                    {
                      color: "#fff",
                      level: 0,
                      id: 0,
                      name: "No Priority",
                    },
                    ...data,
                  ]?.map((item: DictType) => (
                    <CommandItem
                      key={item.id}
                      value={item?.name}
                      onSelect={(currentValue: any) => {
                        setSelectedPriority(item)
                        setOpen(false)
                        updateTaskPriorityMutation.mutate(item?.id)
                      }}
                      className={cn(
                        "my-1 cursor-pointer text-black",
                        item?.color === "#transparent" && "bg-primary",
                      )}
                      style={{ background: item?.color }}
                    >
                      {item.name + ` (${item?.level})`}
                    </CommandItem>
                  ))
                ) : (
                  <></>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='secondary'
          role='combobox'
          aria-expanded={open}
          className='h-8 w-full text-black'
          style={{ background: selectedPriority?.color }}
        >
          {selectedPriority ? selectedPriority?.name : translate("Select priority")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-0'>
        <Command>
          <CommandInput placeholder={translate("Search priority")} />
          <CommandList>
            <CommandEmpty>{translate("No priority found")}.</CommandEmpty>
            <CommandGroup>
              {data?.map((item: DictType) => (
                <CommandItem
                  key={item.id}
                  value={item?.name}
                  onSelect={(currentValue: any) => {
                    setSelectedPriority(item)
                    setOpen(false)
                    setPriority && setPriority(item)
                  }}
                  className={cn("my-1 text-white", item?.color === "#transparent" && "bg-primary")}
                  style={{ background: item?.color }}
                >
                  {item.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default TaskPriorityEditor
